/**
 * 트래킹과 함께 페이지 이동을 수행하는 Hook
 * 
 * TrackedLink와 동일한 ItemSpec 구조를 사용하여 일관성 유지
 * section_nav_click, list_more_click 등의 이벤트를 자동으로 로깅하고 trace_id를 URL에 추가합니다.
 * 
 * @example
 * ```tsx
 * function BriefingAd() {
 *   const { navigateWithTracking } = useTrackedNavigation();
 *   
 *   // 일반 페이지 이동
 *   const handleClick = () => {
 *     navigateWithTracking({
 *       href: '/briefing',
 *       itemspec: {
 *         kind: 'section_nav',
 *         section_id: 'main_feed',
 *         nav_key: 'briefing_ad_click'
 *       }
 *     });
 *   };
 *   
 *   // 뒤로가기
 *   const handleBack = () => {
 *     navigateWithTracking({
 *       href: 'back',
 *       itemspec: {
 *         kind: 'section_nav',
 *         section_id: 'briefing_detail',
 *         nav_key: 'back_button'
 *       }
 *     });
 *   };
 *   
 *   return (
 *     <>
 *       <button onClick={handleClick}>브리핑 보러가기</button>
 *       <button onClick={handleBack}>뒤로가기</button>
 *     </>
 *   );
 * }
 * ```
 */

import { useCallback } from 'react';
import { useRouter } from 'next/router';
import { track } from '../../core/client';
import { ulid } from 'ulid';
import type { ItemSpec } from '../link';

export interface NavigateWithTrackingParams {
    /** 이동할 경로 (예: '/briefing', '/detail/005930', 'back') */
    href: string | 'back';
    /** 클릭 아이템 스펙 (TrackedLink와 동일한 구조) */
    itemspec: ItemSpec;
    /** 클릭 발생 리스트/섹션 ID (optional) */
    fromListId?: string;
    /** trace_id를 URL에 추가할지 여부 (기본: true, back일 때는 무시됨) */
    asTrace?: boolean;
    /** 추가 쿼리 파라미터 (예: { from: 'discuss' }) */
    queryParams?: Record<string, string>;
}

export function useTrackedNavigation() {
    const router = useRouter();

    /**
     * 트래킹과 함께 페이지 이동
     */
    const navigateWithTracking = useCallback((params: NavigateWithTrackingParams) => {
        const {
            href,
            itemspec,
            fromListId,
            asTrace = true,
            queryParams = {},
        } = params;

        const traceId = ulid();

        // 이벤트 종류 결정 및 전송
        const { kind = 'section_nav' } = itemspec;

        switch (kind) {
            case 'section_nav':
                track('section_nav_click', {
                    section_id: itemspec.section_id,
                    nav_key: itemspec.nav_key,
                    trace_id: traceId,
                });
                break;

            case 'list_more':
                track('list_more_click', {
                    list_id: itemspec.list_id || fromListId || '',
                    trace_id: traceId,
                });
                break;

            case 'topic':
                track('topic_click', {
                    topic_id: itemspec.topic_id!,
                    from_list_id: fromListId,
                    rank_pos: itemspec.rank,
                    trace_id: traceId,
                });
                break;

            case 'stock':
                track('stock_click', {
                    ticker: itemspec.ticker!,
                    market: (itemspec.market || 'kr') as 'kr' | 'us',
                    cat: itemspec.cat,
                    from_list_id: fromListId,
                    rank_pos: itemspec.rank,
                    trace_id: traceId,
                    search_intent_id: itemspec.search_intent_id,
                });
                break;

            case 'report':
                track('report_click', {
                    report_id: itemspec.report_id!,
                    from_list_id: fromListId,
                    rank_pos: itemspec.rank,
                    trace_id: traceId,
                    search_intent_id: itemspec.search_intent_id,
                });
                break;

            case 'strategy':
                track('strategy_click', {
                    strategy_id: itemspec.strategy_id!,
                    from_list_id: fromListId,
                    rank_pos: itemspec.rank,
                    trace_id: traceId,
                });
                break;

            case 'newsrader':
                track('newsrader_click', {
                    ticker: itemspec.ticker!,
                    newsrader_id: itemspec.newsrader_id!,
                    from_list_id: fromListId,
                    rank_pos: itemspec.rank,
                    trace_id: traceId,
                });
                break;

            default:
                console.warn(`[useTrackedNavigation] Unknown itemspec.kind: ${kind}`);
                break;
        }

        // 페이지 이동
        if (href === 'back') {
            // 뒤로가기
            router.back();
        } else {
            // 일반 이동
            // const query = new URLSearchParams({
            //     ...queryParams,
            // });

            // const queryString = query.toString();
            // const finalUrl = queryString ? `${href}?${queryString}` : href;

            router.push(href);
        }
    }, [router]);

    /**
     * 트래킹과 함께 외부 링크 이동
     */
    const openWithTracking = useCallback((params: NavigateWithTrackingParams) => {
        const {
            href,
            itemspec,
            fromListId,
            asTrace = true,
            queryParams = {},
        } = params;

        const traceId = ulid();

        // 이벤트 전송 (동일한 로직)
        const { kind = 'section_nav' } = itemspec;

        switch (kind) {
            case 'section_nav':
                track('section_nav_click', {
                    section_id: itemspec.section_id,
                    nav_key: itemspec.nav_key,
                    trace_id: traceId,
                });
                break;

            case 'list_more':
                track('list_more_click', {
                    list_id: itemspec.list_id || fromListId || '',
                    trace_id: traceId,
                });
                break;

            default:
                console.warn(`[useTrackedNavigation] openWithTracking: Unknown kind ${kind}`);
                break;
        }

        // 외부 링크 열기
        if (href === 'back') {
            console.warn('[useTrackedNavigation] openWithTracking does not support "back"');
            return;
        }

        const query = new URLSearchParams({
            ...queryParams,
            ...(asTrace ? { trace: traceId } : {}),
        });

        const queryString = query.toString();
        const finalUrl = queryString ? `${href}?${queryString}` : href;

        window.open(finalUrl, '_blank', 'noopener,noreferrer');
    }, []);

    return {
        navigateWithTracking,
        openWithTracking,
    };
}

